import { useContext } from "react";
import { LangContext } from "./LangContext";
import { cleanText, splitBullets } from "../lib/utils";

const probLabel = (p, lang) => {
  if (lang !== "en") return p;
  return p === "높음" ? "High" : p === "중간" ? "Medium" : p === "낮음" ? "Low" : p;
};

function Bullet({ text, muted }) {
  return (
    <div style={{
      display: "flex", gap: 8,
      fontSize: 13.5, color: muted ? "var(--pc-fg-3-warm)" : "var(--pc-fg-2)",
      lineHeight: 1.6, marginBottom: 3,
    }}>
      <span style={{ flexShrink: 0, color: "var(--pc-fg-4)" }}>·</span>
      <span style={{ flex: 1 }}>{text}</span>
    </div>
  );
}

const PROB_COLOR = {
  "높음": "var(--pc-dir-up)",
  "중간": "var(--pc-fg-2)",
  "낮음": "var(--pc-fg-3)",
};

export default function PCDeeperTab({
  deepLoading, deepError, ach, cuiBono, scenarios, topArticles,
  subScenariosMap, subLoadingIdx, onLoadSubScenarios,
}) {
  const lang = useContext(LangContext);
  if (deepLoading && !scenarios?.length && !ach?.length) {
    return (
      <div style={{ padding: 60, textAlign: "center" }}>
        <span style={{ fontSize: 10, color: "var(--pc-fg-3)", letterSpacing: ".08em", textTransform: "uppercase", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 5, height: 5, background: "var(--pc-fg-3)" }} className="pc-anim-blink" />
          {lang === "en" ? "Analyzing…" : "심화 분석 중"}
        </span>
      </div>
    );
  }
  if (deepError) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "var(--pc-dir-up)", fontSize: 13 }}>
        {lang === "en" ? "Analysis failed — " : "분석 실패 — "}{deepError}
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: "auto" }}>
      <div style={{ maxWidth: 780, margin: "0 auto", padding: "32px 32px 80px" }}>
        {/* 시나리오 예측 */}
        {scenarios?.length > 0 && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
              <div style={{ width: 6, height: 6, borderRadius: 9999, background: "var(--pc-tier-watershed)" }} />
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".18em", textTransform: "uppercase", color: "var(--pc-tier-watershed)" }}>
                {lang === "en" ? "Scenario Forecast" : "시나리오 예측"}
              </span>
              <div style={{ flex: 1, height: 2, background: "rgba(255,255,255,.6)" }} />
            </div>
            {scenarios.map((s, i) => {
              const subList = subScenariosMap?.[i];
              const isSubLoading = subLoadingIdx === i;
              const probCol = PROB_COLOR[s.probability] || "var(--pc-fg-3)";
              return (
                <div key={i} className="pc-anim-fadeUp" style={{ padding: "22px 0", borderBottom: "1px solid var(--pc-line-3)" }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 12 }}>
                    <div style={{ flex: 1, fontSize: 19, fontWeight: 700, color: "var(--pc-fg-1-warm)", lineHeight: 1.3, letterSpacing: "-.005em" }}>
                      {cleanText(s.title)}
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 800, color: probCol, letterSpacing: ".10em", textTransform: "uppercase" }}>
                      {probLabel(s.probability, lang)}
                    </span>
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    {splitBullets(s.reasoning).map((r, j) => <Bullet key={j} text={r} muted />)}
                  </div>

                  {/* Sub-scenarios */}
                  {subList?.length > 0 && subList.map((sub, j) => (
                    <div key={j} className="pc-anim-fadeUp" style={{ marginTop: 14, paddingLeft: 22, position: "relative" }}>
                      <span style={{ position: "absolute", left: 2, top: -1, color: "var(--pc-fg-4)", fontSize: 16 }}>↳</span>
                      <div style={{ display: "flex", gap: 10, alignItems: "baseline", marginBottom: 4 }}>
                        <div style={{ flex: 1, fontSize: 14, fontWeight: 700, color: "var(--pc-fg-1-warm)" }}>
                          {cleanText(sub.title)}
                        </div>
                        {sub.probability && (
                          <span style={{ fontSize: 9, fontWeight: 800, color: PROB_COLOR[sub.probability] || "var(--pc-fg-3)", letterSpacing: ".10em", textTransform: "uppercase" }}>
                            {probLabel(sub.probability, lang)}
                          </span>
                        )}
                      </div>
                      {sub.detail && (
                        <div style={{ fontSize: 13, color: "var(--pc-fg-3-warm)", lineHeight: 1.5 }}>{sub.detail}</div>
                      )}
                    </div>
                  ))}

                  {!subList && !isSubLoading && (
                    <button
                      onClick={() => onLoadSubScenarios(i, s)}
                      style={{
                        background: "transparent", border: "1px solid var(--pc-line-1)",
                        color: "var(--pc-fg-3)", fontSize: 10, fontWeight: 700,
                        padding: "4px 10px", cursor: "pointer", letterSpacing: ".10em", textTransform: "uppercase",
                      }}
                    >More →</button>
                  )}
                  {isSubLoading && (
                    <span style={{ fontSize: 10, color: "var(--pc-fg-3)", letterSpacing: ".08em" }}>{lang === "en" ? "Analyzing…" : "분석 중…"}</span>
                  )}
                </div>
              );
            })}
          </>
        )}

        {/* Framing */}
        {ach?.length > 0 && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "36px 0 18px" }}>
              <div style={{ width: 6, height: 6, borderRadius: 9999, background: "var(--pc-fg-2)" }} />
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".18em", textTransform: "uppercase", color: "var(--pc-fg-2)" }}>
                Framing
              </span>
              <div style={{ flex: 1, height: 2, background: "rgba(255,255,255,.6)" }} />
            </div>
            {ach.map((a, i) => (
              <div key={i} className="pc-anim-fadeUp" style={{ padding: "22px 0", borderBottom: "1px solid var(--pc-line-3)" }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--pc-fg-2)", marginBottom: 10 }}>
                  {cleanText(a.theme || a.lens)}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
                  <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                    <span style={{ color: "var(--pc-dir-down)", fontSize: 13, fontWeight: 800, marginTop: 2, minWidth: 18 }}>A</span>
                    <div style={{ fontSize: 17, fontWeight: 700, color: "var(--pc-fg-1-warm)", lineHeight: 1.4 }}>{cleanText(a.sideA || a.hed)}</div>
                  </div>
                  <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                    <span style={{ color: "var(--pc-dir-up)", fontSize: 13, fontWeight: 800, marginTop: 2, minWidth: 18 }}>B</span>
                    <div style={{ fontSize: 17, fontWeight: 700, color: "var(--pc-fg-1-warm)", lineHeight: 1.4 }}>{cleanText(a.sideB)}</div>
                  </div>
                </div>
                {(a.evidence || []).map((ev, j) => {
                  const evArticle = ev.src ? topArticles?.find(ta => ta.src === ev.src) : null;
                  const side = ev.side || ev.tag;
                  const sideColor = side === "A" ? "var(--pc-dir-down)" : side === "B" ? "var(--pc-dir-up)" : "var(--pc-fg-2)";
                  return (
                    <div key={j} style={{ borderLeft: "1.5px solid var(--pc-line-1)", paddingLeft: 14, display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 10 }}>
                      {side && (
                        <span style={{ color: sideColor, fontSize: 11, fontWeight: 800, marginTop: 3, minWidth: 14 }}>
                          {side}
                        </span>
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13.5, color: "var(--pc-fg-3-warm)", lineHeight: 1.6 }}>{cleanText(ev.text)}</div>
                        {ev.src && (
                          evArticle?.url
                            ? <a href={evArticle.url} target="_blank" rel="noopener noreferrer"
                                 style={{ fontSize: 10, color: "var(--pc-fg-4)", borderBottom: "1px solid var(--pc-line-1)", marginTop: 4, display: "inline-block", letterSpacing: ".05em", textDecoration: "none" }}
                              >{ev.src}</a>
                            : <span style={{ fontSize: 10, color: "var(--pc-fg-4)", marginTop: 4, display: "inline-block", letterSpacing: ".05em" }}>{ev.src}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </>
        )}

        {/* 수혜자 분석 */}
        {cuiBono && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "36px 0 18px" }}>
              <div style={{ width: 6, height: 6, borderRadius: 9999, background: "var(--pc-fg-3)" }} />
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".18em", textTransform: "uppercase", color: "var(--pc-fg-3)" }}>
                {lang === "en" ? "Who Benefits" : "수혜자 분석"}
              </span>
              <div style={{ flex: 1, height: 2, background: "rgba(255,255,255,.6)" }} />
            </div>
            <div className="pc-anim-fadeUp" style={{ padding: "22px 0" }}>
              {typeof cuiBono === "object" ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {cuiBono.beneficiary && (
                    <div style={{ display: "flex", gap: 8, fontSize: 14, color: "var(--pc-fg-1)", fontWeight: 700, marginBottom: 6 }}>
                      <span style={{ color: "var(--pc-fg-4)" }}>·</span>
                      <span>{cleanText(cuiBono.beneficiary)}</span>
                    </div>
                  )}
                  {(cuiBono.context || []).map((c, i) => (
                    <Bullet key={i} text={cleanText(c)} muted />
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 13.5, color: "var(--pc-fg-2)", lineHeight: 1.6 }}>{cleanText(cuiBono)}</div>
              )}
            </div>
          </>
        )}

        {!scenarios?.length && !ach?.length && !cuiBono && !deepLoading && (
          <div style={{ padding: 60, textAlign: "center", color: "var(--pc-fg-3)", fontSize: 13 }}>
            {lang === "en" ? "No analysis data" : "분석 데이터 없음"}
          </div>
        )}
      </div>
    </div>
  );
}
